// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import { isDesktop } from '@/client/platform';
import { Provider } from '@/types';

const CODEX_SUBSCRIPTION_PROVIDER: Provider = {
  id: 'codex-subscription',
  name: 'Codex Subscription',
  apiKey: '',
  apiHost: '',
  authMode: 'oauth_subscription',
  authProviderKey: 'codex',
  description: 'Codex subscription model configuration.',
  is_valid: false,
  model_type: '',
};

export const INIT_PROVODERS: Provider[] = [
  {
    id: 'gemini',
    name: 'Gemini',
    apiKey: '',
    apiHost: 'https://generativelanguage.googleapis.com/v1beta/openai/',
    description: 'Google Gemini model configuration.',
    is_valid: false,
    model_type: '',
  },
  {
    id: 'openai',
    name: 'OpenAI',
    apiKey: '',
    apiHost: 'https://api.openai.com/v1',
    description: 'OpenAI model configuration.',
    is_valid: false,
    model_type: '',
  },
  ...(isDesktop() ? [CODEX_SUBSCRIPTION_PROVIDER] : []),
  {
    id: 'anthropic',
    name: 'Anthropic',
    apiKey: '',
    apiHost: 'https://api.anthropic.com',
    description: 'Anthropic Claude API configuration',
    is_valid: false,
    model_type: '',
  },
  {
    id: 'grok',
    name: 'Grok',
    apiKey: '',
    apiHost: 'https://api.x.ai/v1',
    description: 'Grok model configuration.',
    is_valid: false,
    model_type: '',
  },
  {
    id: 'ModelArk',
    name: 'ModelArk',
    apiKey: '',
    apiHost: 'https://ark.ap-southeast.bytepluses.com/api/v3',
    description: 'ModelArk model configuration.',
    is_valid: false,
    model_type: '',
  },
  {
    id: 'tongyi-qianwen',
    name: 'Qwen',
    apiKey: '',
    apiHost: 'https://dashscope.aliyuncs.com/compatible-mode/v1',
    description: 'Qwen model configuration.',
    is_valid: false,
    model_type: '',
  },
  {
    id: 'z.ai',
    name: 'Z.ai',
    apiKey: '',
    apiHost: 'https://api.z.ai/api/coding/paas/v4/',
    description: 'Z.ai model configuration.',
    is_valid: false,
    model_type: '',
  },
  {
    id: 'moonshot',
    name: 'Moonshot',
    apiKey: '',
    apiHost: 'https://api.moonshot.ai/v1',
    description: 'Kimi model configuration.',
    is_valid: false,
    model_type: '',
  },
  {
    id: 'minimax',
    name: 'Minimax',
    apiKey: '',
    apiHost: 'https://api.minimax.io/v1',
    description: 'Minimax model configuration.',
    is_valid: false,
    model_type: '',
  },
  {
    id: 'deepseek',
    name: 'Deepseek',
    apiKey: '',
    apiHost: 'https://api.deepseek.com',
    description: 'DeepSeek model configuration.',
    is_valid: false,
    model_type: '',
  },
  {
    id: 'samba-nova',
    name: 'SambaNova',
    apiKey: '',
    apiHost: 'https://api.sambanova.ai/v1',
    description: 'SambaNova model configuration.',
    is_valid: false,
    model_type: '',
  },
  {
    id: 'mistral',
    name: 'Mistral',
    apiKey: '',
    apiHost: 'https://api.mistral.ai',
    description: 'Mistral model configuration.',
    is_valid: false,
    model_type: '',
  },
  {
    id: 'openrouter',
    name: 'OpenRouter',
    apiKey: '',
    apiHost: 'https://openrouter.ai/api/v1',
    description: 'OpenRouter model configuration.',
    is_valid: false,
    model_type: '',
  },
  {
    id: 'aws-bedrock',
    name: 'AWS Bedrock',
    apiKey: '',
    apiHost: 'https://bedrock-mantle.us-east-1.api.aws/v1',
    description: 'AWS Bedrock model configuration.',
    is_valid: false,
    model_type: '',
  },
  {
    id: 'aws-bedrock-converse',
    name: 'AWS Bedrock Converse',
    apiKey: '',
    apiHost: 'https://bedrock-runtime.us-east-1.amazonaws.com',
    description:
      'AWS Bedrock Converse model configuration. Auth: API Key (Bearer Token), or Access Key ID + Secret Access Key (+Session Token).',
    externalConfig: [
      {
        key: 'region_name',
        name: 'Region',
        value: 'us-east-1',
      },
      {
        key: 'aws_access_key_id',
        name: 'Access Key ID',
        value: '',
        secret: true,
      },
      {
        key: 'aws_secret_access_key',
        name: 'Secret Access Key',
        value: '',
        secret: true,
      },
      {
        key: 'aws_session_token',
        name: 'Session Token (Optional)',
        value: '',
        secret: true,
      },
    ],
    is_valid: false,
    model_type: '',
  },
  {
    id: 'azure',
    name: 'Azure',
    apiKey: '',
    apiHost: '',
    description: 'Azure OpenAI model configuration.',
    hostPlaceHolder: 'e.g.https://{{your-resource-name}}.openai.azure.com',
    externalConfig: [
      {
        key: 'api_version',
        name: 'API Version',
        value: '',
      },
      {
        key: 'azure_deployment_name',
        name: 'Deployment Name',
        value: '',
      },
    ],
    is_valid: false,
    model_type: '',
  },
  {
    id: 'ernie',
    name: 'Ernie',
    apiKey: '',
    apiHost: 'https://qianfan.baidubce.com/v2',
    description: 'Baidu Ernie model configuration.',
    is_valid: false,
    model_type: '',
  },
  {
    id: 'orcarouter',
    name: 'OrcaRouter',
    apiKey: '',
    apiHost: 'https://api.orcarouter.ai/v1',
    description: 'OrcaRouter model configuration.',
    is_valid: false,
    model_type: '',
    modelsEndpoint: '/models',
    websiteUrl: 'https://www.orcarouter.ai',
  },
  {
    id: 'meta',
    name: 'Meta',
    apiKey: '',
    apiHost: 'https://api.meta.ai/v1',
    description: 'Meta Model API model configuration.',
    is_valid: false,
    model_type: '',
    modelsEndpoint: '/models',
    modelIdPrefix: 'muse-spark-',
    websiteUrl: 'https://dev.meta.ai/docs/quickstart',
  },
  {
    id: 'nebius',
    name: 'Nebius Token Factory',
    apiKey: '',
    apiHost: 'https://api.tokenfactory.nebius.com/v1',
    description: 'Nebius Token Factory model configuration.',
    is_valid: false,
    model_type: '',
    modelsEndpoint: '/models',
    websiteUrl: 'https://docs.tokenfactory.nebius.com/quickstart',
  },
  {
    id: 'ant-ling',
    name: 'Ant Ling',
    apiKey: '',
    apiHost: 'https://api.ant-ling.com/v1',
    description: 'Ant Ling model configuration',
    is_valid: false,
    model_type: '',
    modelsEndpoint: '/models',
    websiteUrl: 'https://developer.ant-ling.com',
  },
  {
    id: 'openai-compatible-model',
    name: 'OpenAI Compatible',
    apiKey: '',
    apiHost: '',
    description: 'OpenAI-compatible API endpoint configuration.',
    hostPlaceHolder: 'e.g. https://api.x.ai/v1',
    is_valid: false,
    model_type: '',
  },
];
